/**
 * Integration Data Cache Utility (Story 4.12)
 *
 * Shared cache-around utility for wrapping adapter fetchMetrics() calls with
 * Cloudflare KV caching. Provides per-platform soft TTLs, 7-day hard expiration,
 * stale-serve on retryable API errors, and fail-open KV access.
 *
 * ADR-016: Aggressive Caching for Resilience
 * ADR-013: Adapter Pattern for API Isolation
 *
 * Security:
 * - Cache keys include JWT-derived tenantId (never from user input)
 * - Cached payload embeds tenantId, verified on read (defense-in-depth)
 * - Non-retryable errors (token_expired, permission_denied) bypass stale-serve
 */
import type { Platform } from "@provenleap/db";
import type { AdapterError, AdapterMetrics, AdapterResult } from "./adapter";
import { sanitizeErrorMessage } from "./safe-fetch";
import { adapterErrorToAppError } from "./errors";
import { AppError } from "@provenleap/types";

// ============================================================================
// KV Interface (avoids coupling to @cloudflare/workers-types for web consumers)
// ============================================================================

/**
 * Minimal KV store interface matching Cloudflare KVNamespace.
 * Defined here so web-app tsconfig doesn't need @cloudflare/workers-types.
 */
export interface KVStore {
  get(key: string, options?: { type?: string }): Promise<string | null>;
  put(key: string, value: string, options?: { expirationTtl?: number }): Promise<void>;
  delete(key: string): Promise<void>;
  list(options?: { prefix?: string; cursor?: string }): Promise<{
    keys: { name: string }[];
    list_complete: boolean;
    cursor?: string;
  }>;
}

// ============================================================================
// Types
// ============================================================================

/**
 * Serialized metrics stored in KV. Date objects are converted to ISO strings
 * for JSON serialization. Use hydrateCacheEntry() to reconstruct Dates on read.
 */
export interface CachedMetricsEntry {
  data: {
    platform: string;
    accountId: string;
    dateRange: { start: string; end: string };
    records: Record<string, unknown>[];
    fetchedAt: string;
    recordCount: number;
  };
  /** Embedded for defense-in-depth tenant verification on cache read */
  tenantId: string;
  /** Unix milliseconds for TTL comparison */
  fetchedAt: number;
  platform: string;
}

/**
 * Result returned by fetchMetricsWithCache().
 * Includes cache metadata alongside the adapter metrics data.
 */
export interface CacheResult {
  data: AdapterMetrics;
  fromCache: boolean;
  stale: boolean;
  cachedAt: string | null;
}

// ============================================================================
// Constants
// ============================================================================

/** Non-retryable adapter error codes — stale-serve MUST NOT be used for these */
const NON_RETRYABLE_CODES = new Set([
  "token_expired",
  "token_invalid",
  "permission_denied",
  "api_not_enabled",
  "account_not_found",
]);

/**
 * Per-platform soft TTL in seconds.
 * Reflects actual data freshness characteristics of each platform.
 */
export const PLATFORM_CACHE_TTL: Record<string, number> = {
  "google-analytics-4": 3600, // 1 hour — data has 24-48h processing lag but updates intraday
  "google-search-console": 14400, // 4 hours — data has 2-3 day lag, very stable
  "google-ads": 3600, // 1 hour — spend data updates intraday
  "youtube-analytics": 14400, // 4 hours — data has 24-48h lag
  "google-business-profile": 14400, // 4 hours — data has 24h+ lag
  "google-reviews": 14400, // 4 hours — Story 23.1. Reviews data has the same lag profile as GBP performance metrics (24h+); shares Google's per-project 300 QPM budget so aggressive caching protects the GBP integration's headroom too. NO 30-day ToS cap — uses the global 7-day stale fallback (no STALE_EXPIRATION_TTL_BY_PLATFORM entry).
  "google-pagespeed": 604800, // 7 days — manual refresh model
  "facebook-pages": 3600, // 1 hour — data has 24h+ lag but page metrics update frequently
  "facebook-ads": 3600, // 1 hour — spend data updates intraday, matches Google Ads
  "instagram-business": 3600, // 1 hour — data has 24h+ lag but engagement metrics update frequently
  "instagram-ads": 3600, // 1 hour — spend data updates intraday, same as Facebook Ads
  "linkedin-pages": 7200, // 2 hours — LinkedIn has low rate limits (~100 req/day), data has 24h+ lag
  "linkedin-ads": 7200, // 2 hours — shares LinkedIn's ~100 req/day rate limit budget
  "tiktok-business": 3600, // 1 hour — TikTok has 24h token, generous rate limits (600/min)
  "mailchimp": 7200, // 2 hours — Mailchimp data has 24h+ lag, 10 simultaneous connections limit
  "klaviyo": 7200, // 2 hours — Klaviyo metric-aggregates uses event time, 10 req/s metrics endpoint
  "shopify": 3600, // 1 hour — Shopify data updates near-real-time
  "microsoft-ads": 3600, // 1 hour — Bing Ads spend updates intraday (placeholder for Story 19.2 metric fetching)
  "pinterest-ads": 3600, // 1 hour — Pinterest spend updates intraday, matches Google Ads / Meta Ads (placeholder for Story 19.5 metric fetching)
  "snapchat-ads": 3600, // 1 hour — Snap spend updates intraday, matches Google Ads / Meta Ads / Pinterest Ads (placeholder for Story 19.8 metric fetching)
  "reddit-ads": 3600, // 1 hour — Reddit spend updates intraday, matches Google Ads / Meta Ads / Pinterest / Snapchat Ads (placeholder for Story 19.11 metric fetching)
  "amazon-ads": 3600, // 1 hour primary cache TTL — Amazon SP report data refreshes ~1h, SB/SD ~3h, DSP ~12h. The per-report-type cache layer with longer TTLs lands in Story 19.14 (placeholder for Stories 19.14/19.15/19.16 metric fetching).
  "ahrefs": 14400, // 4 hours — Ahrefs crawl-driven metrics update at-most-daily (DR recalibrates ~weekly). Same TTL as linkedin-pages and youtube-analytics. Caching aggressively also conserves the customer's monthly Enterprise unit quota — 4× slower burn vs. 1h TTL.
  "semrush": 14400, // 4 hours — Semrush crawl-driven metrics update at-most-daily; Authority Score recalibrates ~weekly per Semrush blog. Same TTL as Ahrefs (Story 20.1). Aggressive caching conserves the customer's monthly unit quota.
  "moz": 86400, // 24 hours — Moz Index refreshes monthly per moz.com/help/links-api/making-calls/index-metadata (verified 2026-05-10); DA/PA/Spam Score do NOT change between refreshes. Aggressive caching conserves the customer's monthly row quota AND aligns with Moz's update cadence. NOTE: Moz has NO 30-day ToS data-persistence cap (verified moz.com/legal/terms 2026-05-10) — uses the global 7-day stale fallback (no STALE_EXPIRATION_TTL_BY_PLATFORM entry).
  "callrail": 3600, // 1 hour — CallRail processes calls near-real-time with a 20-minute post-call webhook ceiling per support.callrail.com/hc/en-us/articles/5711246459149-Webhooks (verified 2026-05-14). Matches Shopify's realtime-leaning posture. NOTE: CallRail has NO documented 30-day ToS data-persistence cap (verified www.callrail.com/legal/terms-of-service 2026-05-14) — uses the global 7-day stale fallback (no STALE_EXPIRATION_TTL_BY_PLATFORM entry).
  "calltrackingmetrics": 3600, // 1 hour — CTM processes calls, SMS, and form submissions near-real-time. Matches CallRail 22.1's posture. NOTE: CTM has NO documented 30-day ToS data-persistence cap (verified www.calltrackingmetrics.com/legal/data-protection-addendum-2025/ 2026-05-15 — no equivalent to Semrush ToS §3.3) — uses the global 7-day stale fallback (no STALE_EXPIRATION_TTL_BY_PLATFORM entry). Story 22.3.
  "trustpilot": 14400, // 4 hours — Story 23.3. TrustScore is recalculated DAILY per developers.trustpilot.com/faq/ verified 2026-05-16; 4-hour TTL conserves the customer's 10K-req/hr quota during widget re-renders (8 fetches across 24h vs. 24 at 1h TTL). Reviews update near-real-time but the aggregate TrustScore doesn't move within 4 hours unless a brand-new review hits an inflection point. Matches Google Reviews 23.1's posture. NO 30-day ToS data-persistence cap (verified legal.trustpilot.com/end-user-terms-and-conditions + business.trustpilot.com/terms 2026-05-16 — no equivalent to Semrush ToS §3.3) — uses the global 7-day stale fallback (no STALE_EXPIRATION_TTL_BY_PLATFORM entry).
  "yelp": 21600, // 6 hours — Story 23.5. Yelp ratings update in near-real-time but the daily-call budget makes aggressive refresh wasteful (Starter 300/day = 12.5/hr divided across health-checks + N integrations). 6h matches the daily-budget arithmetic: 4 fetches/24h × N integrations stays under the 250/day per-tenant circuit-breaker ceiling with headroom for the 96 daily health-checks per integration (15-min cron tick). 5-QPS burst ceiling per docs.developer.yelp.com/docs/fusion-rate-limiting also discourages tighter TTLs. NO data-persistence ToS cap (verified docs.developer.yelp.com/docs/fusion-terms 2026-05-18) — uses the global 7-day stale fallback (no STALE_EXPIRATION_TTL_BY_PLATFORM entry).
};

/** Hard KV expiration in seconds (7 days). Allows stale-serve during API outages. */
export const STALE_EXPIRATION_TTL = 7 * 24 * 3600; // 604800 seconds

/**
 * Per-platform stale-TTL ceiling override (Story 20.4 AC #16).
 *
 * Some providers contractually constrain how long we may persist their data.
 * The `STALE_EXPIRATION_TTL` global default (7 days) is the floor; any entry
 * here is the platform-specific ceiling enforced by `getStaleExpirationTtl`.
 *
 * **Semrush ToS §3.3 (verified 2026-05-08 via
 * https://www.semrush.com/company/legal/terms-of-service/):**
 *   "you agree not to cache the information received from the Services for
 *    more than one (1) month without the express written consent of Semrush."
 *
 * 30 days = 2,592,000 seconds is the absolute hard ceiling for `"semrush"`.
 * The current 7-day global is already compliant; this override exists so a
 * future story raising the global past 30 days does not silently violate
 * the Semrush contract.
 *
 * **This is a build-to-sell decision (legal-contract compliance), not just
 * an operational tuning.** A buyer's diligence team will look for evidence
 * we read the ToS — keep this entry and its citation block stable.
 */
export const STALE_EXPIRATION_TTL_BY_PLATFORM: Record<string, number> = {
  "semrush": 2_592_000, // 30 days exactly — Semrush ToS §3.3 (2026-05-08).
};

/**
 * Resolve the stale-expiration TTL for a given platform.
 *
 * Returns the per-platform override when present, otherwise the global
 * default. Pure function — safe to call from cache-key construction without
 * fear of side effects or async I/O.
 */
export function getStaleExpirationTtl(platform: string): number {
  const override = STALE_EXPIRATION_TTL_BY_PLATFORM[platform];
  return override !== undefined ? override : STALE_EXPIRATION_TTL;
}

/** Refresh cooldown in milliseconds (1 hour) */
export const REFRESH_COOLDOWN_MS = 60 * 60 * 1000;

/** Maximum cache payload size in bytes (20 MB warning threshold, KV limit is 25 MB) */
export const MAX_CACHE_PAYLOAD_BYTES = 20 * 1024 * 1024;

// ============================================================================
// Cache Key Construction
// ============================================================================

/**
 * Builds a cache key for integration metrics.
 * Format: `metrics:{tenantId}:{integrationId}:{startDate}_{endDate}`
 *
 * The tenantId MUST come from verified JWT context (c.get("tenantId")),
 * never from request parameters.
 */
export function buildCacheKey(
  tenantId: string,
  integrationId: string,
  startDate: string,
  endDate: string
): string {
  return `metrics:${tenantId}:${integrationId}:${startDate}_${endDate}`;
}

/**
 * Builds a cache key for PageSpeed metrics (no date range — point-in-time).
 * Format: `metrics:{tenantId}:{integrationId}:pagespeed`
 */
export function buildPageSpeedCacheKey(
  tenantId: string,
  integrationId: string
): string {
  return `metrics:${tenantId}:${integrationId}:pagespeed`;
}

/**
 * Builds a cache key for the agency-wide dashboard overview rollup (Story 8.1).
 * Format: `overview:{tenantId}`
 *
 * `tenantId` is kept in the SEGMENT-AFTER-NAMESPACE position (NOT appended last)
 * — the same layout `buildCacheKey` uses for `metrics:`. `invalidateTenantCache`
 * sweeps this key alongside metrics during a GDPR tenant-deletion via an
 * EXACT-key get-then-delete (NOT a bare `overview:${tenantId}` prefix scan,
 * which would over-match siblings — `overview:t1` matching `overview:t10`).
 *
 * The tenantId MUST come from verified JWT context (c.get("tenantId")),
 * never from request parameters.
 */
export function buildAgencyOverviewCacheKey(tenantId: string): string {
  return `overview:${tenantId}`;
}

// Story 19.14 — Amazon Ads SP cache helpers live in `./amazon/reports.ts`
// because their key shape encodes the v3-request `request_hash`. They are
// re-exported here so consumers don't need to know their physical location.
export {
  buildAmazonAdsSpCacheKey,
  computeAmazonAdsSpCacheTtl,
  AMAZON_ADS_SP_CACHE_TTL_FRESH_SEC,
  AMAZON_ADS_SP_CACHE_TTL_FINALIZED_SEC,
} from "./amazon/reports";

// ============================================================================
// Serialization / Deserialization
// ============================================================================

/**
 * Serializes AdapterMetrics for KV storage.
 * Converts Date objects to ISO strings for JSON compatibility.
 */
function serializeMetrics(
  metrics: AdapterMetrics
): CachedMetricsEntry["data"] {
  return {
    platform: metrics.platform,
    accountId: metrics.accountId,
    dateRange: {
      start: metrics.dateRange.start.toISOString(),
      end: metrics.dateRange.end.toISOString(),
    },
    records: metrics.records,
    fetchedAt: metrics.fetchedAt.toISOString(),
    recordCount: metrics.recordCount,
  };
}

/**
 * Reconstructs AdapterMetrics from cached data.
 * Converts ISO strings back to Date objects.
 */
export function hydrateCacheEntry(
  data: CachedMetricsEntry["data"]
): AdapterMetrics {
  return {
    platform: data.platform as Platform,
    accountId: data.accountId,
    dateRange: {
      start: new Date(data.dateRange.start),
      end: new Date(data.dateRange.end),
    },
    records: data.records,
    fetchedAt: new Date(data.fetchedAt),
    recordCount: data.recordCount,
  };
}

// ============================================================================
// Error Classification
// ============================================================================

/**
 * Determines whether an error is retryable (eligible for stale-serve).
 * Non-retryable errors (token_expired, permission_denied) must surface immediately.
 *
 * Design trade-off: Unknown errors (no `code` field) default to retryable=true.
 * This is a deliberate availability-over-correctness choice — stale cached data is
 * preferred over a hard failure when we can't classify the error. The risk is serving
 * stale data after a non-retryable error that lacks a proper code, but this is bounded
 * by the 7-day hard TTL and is preferable to failing the entire request.
 */
function isRetryableError(error: unknown): boolean {
  if (
    error &&
    typeof error === "object" &&
    "code" in error &&
    typeof (error as AdapterError).code === "string"
  ) {
    return !NON_RETRYABLE_CODES.has((error as AdapterError).code);
  }
  // Unknown errors are treated as retryable (fail-open for stale-serve)
  return true;
}

// ============================================================================
// Structured Logging
// ============================================================================

type CacheEvent =
  | "cache_hit"
  | "cache_miss"
  | "cache_stale"
  | "cache_write"
  | "cache_error"
  | "cache_invalidate";

function logCacheEvent(
  event: CacheEvent,
  details: {
    cacheKey: string;
    platform?: string;
    tenantId?: string;
    latencyMs?: number;
    error?: string;
  }
): void {
  console.log(
    JSON.stringify({
      event,
      ...details,
      timestamp: new Date().toISOString(),
    })
  );
}

// ============================================================================
// Core Cache Utility
// ============================================================================

/**
 * Wraps an adapter's fetchMetrics() call with KV caching.
 *
 * Flow:
 * 1. Check KV cache (fail-open)
 * 2. Verify tenant ownership on cached data (defense-in-depth)
 * 3. If fresh cache hit → return cached data immediately
 * 4. If cache miss or stale → call fetcher (the adapter's fetchMetrics)
 * 5. On success → write to KV cache (fail-open), return fresh data
 * 6. On failure + retryable error + stale cache exists → return stale data
 * 7. On failure + non-retryable error → throw immediately
 *
 * @param kv - Cloudflare KV namespace binding
 * @param cacheKey - Pre-built cache key from buildCacheKey()
 * @param platform - Platform identifier for TTL lookup
 * @param tenantId - Verified tenant ID from JWT context
 * @param fetcher - The adapter.fetchMetrics() call wrapped in a thunk
 * @param forceRefresh - If true, skip cache read and always call fetcher (AC #4)
 */
export async function fetchMetricsWithCache(
  kv: KVStore,
  cacheKey: string,
  platform: string,
  tenantId: string,
  fetcher: () => Promise<AdapterResult<AdapterMetrics>>,
  forceRefresh = false
): Promise<CacheResult> {
  const startTime = Date.now();
  const softTtl = PLATFORM_CACHE_TTL[platform] ?? 3600;

  // 1. Check cache (fail-open) — skip on forceRefresh but still load for stale-serve fallback
  let cached: CachedMetricsEntry | null = null;
  try {
    const raw = await kv.get(cacheKey, { type: "text" });
    if (raw) {
      cached = JSON.parse(raw);
    }
  } catch (err) {
    logCacheEvent("cache_error", {
      cacheKey,
      platform,
      tenantId,
      error: sanitizeErrorMessage(
        err instanceof Error ? err.message : String(err)
      ),
    });
    // Fail-open: treat as cache miss
  }

  // 2. Verify tenant ownership on cached data (defense-in-depth)
  if (cached && cached.tenantId !== tenantId) {
    logCacheEvent("cache_error", {
      cacheKey,
      platform,
      tenantId,
      error: "Tenant mismatch in cached data — treating as cache miss",
    });
    cached = null;
  }

  // 3. If fresh cache hit, return immediately (skip on forceRefresh — AC #4)
  if (!forceRefresh && cached && Date.now() - cached.fetchedAt < softTtl * 1000) {
    logCacheEvent("cache_hit", {
      cacheKey,
      platform,
      tenantId,
      latencyMs: Date.now() - startTime,
    });
    return {
      data: hydrateCacheEntry(cached.data),
      fromCache: true,
      stale: false,
      cachedAt: new Date(cached.fetchedAt).toISOString(),
    };
  }

  // 4. Cache miss or stale — try API
  try {
    const result = await fetcher();

    if (!result.success) {
      // Adapter returned an error result — treat as API failure
      const adapterError: AdapterError = result.error;

      // If retryable + stale cache exists → serve stale
      if (cached && isRetryableError(adapterError)) {
        logCacheEvent("cache_stale", {
          cacheKey,
          platform,
          tenantId,
          latencyMs: Date.now() - startTime,
        });
        return {
          data: hydrateCacheEntry(cached.data),
          fromCache: true,
          stale: true,
          cachedAt: new Date(cached.fetchedAt).toISOString(),
        };
      }

      // Non-retryable or no stale cache — convert to AppError for HTTP error handler
      throw adapterErrorToAppError(adapterError);
    }

    // 5. Write to cache (fail-open)
    const entry: CachedMetricsEntry = {
      data: serializeMetrics(result.data),
      tenantId,
      fetchedAt: Date.now(),
      platform,
    };

    try {
      const serialized = JSON.stringify(entry);

      if (serialized.length > MAX_CACHE_PAYLOAD_BYTES) {
        logCacheEvent("cache_error", {
          cacheKey,
          platform,
          tenantId,
          error: `Payload exceeds ${MAX_CACHE_PAYLOAD_BYTES} bytes (${serialized.length} bytes) — skipping cache write`,
        });
      } else {
        // TTL jitter: ±20% to prevent synchronized cache expiry (thundering herd).
        //
        // **Story 20.4 AC #16 — per-platform stale-TTL ceiling:** honor
        // `STALE_EXPIRATION_TTL_BY_PLATFORM[platform]` (e.g. Semrush ToS §3.3
        // 30-day cap = 2,592,000s). The jittered TTL is computed against the
        // platform-specific ceiling, falling back to the 7-day global default.
        // For platforms without an override, the existing 7-day jittered TTL
        // is unchanged. NEVER raise the global past 30 days without simultaneously
        // verifying every override-platform's contractual ceiling.
        //
        // **Code Review Pass 2 (Critical):** for platforms with a contractual
        // ceiling, the +20% jitter would push the TTL OVER the ceiling
        // (Semrush: 30 days × 1.2 = 36 days, breaching ToS §3.3 by 6 days).
        // For override platforms, jitter is one-sided: `baseTtl × (0.8 + 0..0.2)`
        // — the high bound is exactly `baseTtl`, never above. Platforms
        // without an override keep the original ±20% bidirectional jitter.
        const baseTtl = getStaleExpirationTtl(platform);
        const hasOverride = STALE_EXPIRATION_TTL_BY_PLATFORM[platform] !== undefined;
        const jitterFactor = hasOverride
          ? 0.8 + Math.random() * 0.2 // [0.8, 1.0] — never exceeds the ceiling
          : 0.8 + Math.random() * 0.4; // [0.8, 1.2] — original ±20% bidirectional
        const jitteredTtl = Math.round(baseTtl * jitterFactor);
        await kv.put(cacheKey, serialized, { expirationTtl: jitteredTtl });

        logCacheEvent("cache_write", {
          cacheKey,
          platform,
          tenantId,
          latencyMs: Date.now() - startTime,
        });
      }
    } catch (err) {
      // Fail-open: write failure is non-blocking
      logCacheEvent("cache_error", {
        cacheKey,
        platform,
        tenantId,
        error: sanitizeErrorMessage(
          err instanceof Error ? err.message : String(err)
        ),
      });
    }

    logCacheEvent("cache_miss", {
      cacheKey,
      platform,
      tenantId,
      latencyMs: Date.now() - startTime,
    });

    return {
      data: result.data,
      fromCache: false,
      stale: false,
      cachedAt: null,
    };
  } catch (error) {
    // Re-throw AppErrors immediately (already mapped by adapterErrorToAppError)
    if (error instanceof AppError) {
      throw error;
    }

    // 6. API threw — serve stale if available (retryable errors only)
    if (cached && isRetryableError(error)) {
      logCacheEvent("cache_stale", {
        cacheKey,
        platform,
        tenantId,
        latencyMs: Date.now() - startTime,
      });
      return {
        data: hydrateCacheEntry(cached.data),
        fromCache: true,
        stale: true,
        cachedAt: new Date(cached.fetchedAt).toISOString(),
      };
    }
    throw error;
  }
}

// ============================================================================
// Cache Invalidation
// ============================================================================

/**
 * Invalidates all cache entries for a specific integration.
 * Used when an integration is disconnected.
 *
 * Uses KV.list({ prefix }) + KV.delete() to find and remove all matching keys.
 */
export async function invalidateIntegrationCache(
  kv: KVStore,
  tenantId: string,
  integrationId: string
): Promise<number> {
  const startTime = Date.now();
  let deletedCount = 0;

  /**
   * Per-integration KV prefixes to purge on disconnect.
   *
   * **Story 20.4 AC #9 — Semrush per-platform unit-budget + metadata KV
   * keys:** `semrush:units_used:integration:{id}:*` (daily Analytics units),
   * `semrush:trends_units_used:integration:{id}:*` (monthly Trends units),
   * `semrush:meta:{id}` (defaultDatabase + trendsEntitled side-channel).
   * Without explicit purge, a re-connect on the same integration would
   * inherit stale unit-spend tallies and cause a false "budget exceeded"
   * trip on the very first sync.
   *
   * Per-tenant unit-budget keys (`semrush:units_used:tenant:*`) are NOT
   * purged here — they are shared across all the tenant's Semrush
   * integrations and keep their integrity through individual disconnects.
   * They naturally expire at their 60-day TTL.
   *
   * **Code Review Pass 1 (Critical AC #9) — added 2026-05-08.**
   */
  const prefixesToInvalidate: ReadonlyArray<string> = [
    `metrics:${tenantId}:${integrationId}:`,
    `semrush:units_used:integration:${integrationId}:`,
    `semrush:trends_units_used:integration:${integrationId}:`,
    // Story 20.7 AC #9 — Moz per-platform row-budget + async-poll + metadata
    // KV keys. Without explicit purge, a re-connect would inherit stale
    // row-spend tallies and cause a false "budget exceeded" trip on the very
    // first sync. Mirrors Semrush per-integration purge contract above.
    `moz:rows_used:integration:${integrationId}:`,
    `moz:async_call:${integrationId}:`,
    // Story 23.5 AC #8 — Yelp per-tenant daily-call-budget sweep prefix.
    // The budget counter lives under `yelp:budget:${tenantId}:${YYYY-MM-DD}`
    // (tenant-scoped, not integration-scoped — when ONE Yelp integration is
    // disconnected the other tenant integrations should keep their daily
    // count). For per-integration disconnect we cannot safely purge
    // tenant-scoped counters without affecting siblings, so this prefix is
    // intentionally NOT scanned for the per-integration disconnect path.
    // The healthcheck-budget keyspace mirrors the same tenant-scoped shape.
    // Both naturally expire at midnight UTC TTL.
    // Story 22.1 AC #9 — CallRail does NOT have a per-row or per-unit
    // budget (CallRail bills only by request count, enforced by the queue
    // rate-limit throttler), so there are NO `callrail:rows_used:*` or
    // `callrail:units_used:*` prefixes to purge — that absence is
    // documented in callrail/cache-policy.ts. The only CallRail-specific
    // KV side-channels are `callrail:meta:{integrationId}` (handled via
    // exactKeysToInvalidate below) and `callrail:probe:{tenantId}:*`
    // (in-flight probe nonces — tenant-scoped, not integration-scoped, so
    // this purge cannot reach them safely; they expire at the 5-min TTL
    // and are not security-sensitive on the original tenant's own data).
    // Story 22.3 AC #9 — CallTrackingMetrics: same shape as CallRail.
    // NO per-row or per-unit budget (CTM bills only by request count,
    // unpublished but ~300 req/min per account soft cap; enforced by
    // the queue rate-limit throttler). The only CTM-specific KV
    // side-channels are `ctm:meta:{integrationId}` (handled via
    // exactKeysToInvalidate below) and `ctm:probe:{tenantId}:*`
    // (in-flight probe nonces — same tenant-scope reasoning as CallRail).
    // Story 23.3 AC #9 — Trustpilot: same shape as CallRail/CTM in that
    // there is NO per-row or per-unit budget (Trustpilot bills only by
    // request count, 833/5min + 10K/hr enforced by the queue rate-limit
    // throttler). The Trustpilot-specific KV side-channels are
    // `trustpilot:meta:{integrationId}` AND `trustpilot:credentials:
    // {integrationId}` (the encrypted API Key + Secret pair — handled
    // via exactKeysToInvalidate below; CRITICAL — failure to purge
    // leaves the customer's secrets sitting in KV after disconnect,
    // violating GDPR right-to-erasure for the credentials material
    // itself) AND `trustpilot:probe:{tenantId}:*` (in-flight probe
    // nonces — same tenant-scope reasoning as CallRail/CTM; not purged
    // here because they expire at 5-min TTL).
  ];
  // Single-key deletes (no prefix scan needed) — current Semrush metadata
  // side-channel + Ahrefs Brand Radar discovery cache aren't keyed by
  // integrationId in a list-friendly shape.
  // Story 20.5 Code Review Pass 2: also purge the discovery cache (5-min
  // TTL holds the discoverEndpoints shape including `trendsEntitled`
  // badges) so re-auth / Trends-entitlement flips surface immediately.
  const exactKeysToInvalidate: ReadonlyArray<string> = [
    `semrush:meta:${integrationId}`,
    `semrush:discovery:${tenantId}:${integrationId}`,
    // Story 20.7 AC #9 — Moz metadata side-channel (defaultScope +
    // brandAuthorityEntitled + planTier + lastQuotaCheckRows) and discovery
    // cache (5-min TTL holding the 4-endpoint discoverEndpoints shape).
    `moz:meta:${integrationId}`,
    `moz:discovery:${tenantId}:${integrationId}`,
    // Story 22.1 AC #9 — CallRail metadata side-channel (allowedAccountIds,
    // conversationIntelligenceEntitled, companyId, discoveredAt). Mirrors
    // the Moz 20.7 metadata-side-channel pattern; the `integrations`
    // table has no `metadata` JSON column.
    //
    // Pass 1 review fix: previously also listed `callrail:discovery:*`
    // here but no code path writes that key — `discoverEndpoints` returns
    // a fresh-computed snapshot from the live KV metadata read, no
    // intermediate discovery cache layer exists for CallRail (unlike
    // Moz/Semrush which DO cache discovery results to avoid repeated
    // entitlement probes). Removed the dead reference.
    `callrail:meta:${integrationId}`,
    // Story 22.3 AC #9 — CTM metadata side-channel (allowedAccountIds,
    // conversationIntelligenceEntitled, region, keyScope, discoveredAt).
    // Mirrors the CallRail 22.1 metadata-side-channel pattern; the
    // `integrations` table has no `metadata` JSON column. No
    // `ctm:discovery:*` entry because Story 22.3's discoverEndpoints
    // returns a fresh-computed snapshot from live KV metadata reads —
    // no intermediate discovery cache layer (matches CallRail 22.1).
    `ctm:meta:${integrationId}`,
    // Story 23.3 AC #9 — Trustpilot metadata side-channel (businessUnitId
    // + displayName + country + privateApiEntitled + allowedBusinessUnitIds
    // + discoveredAt + domain + defaultLanguage). PLUS the secondary
    // credentials side-channel holding the encrypted API Key + Secret
    // pair — CRITICAL to purge per GDPR right-to-erasure since these
    // are long-lived secrets the cron recoveryRefresh handler reads.
    `trustpilot:meta:${integrationId}`,
    `trustpilot:credentials:${integrationId}`,
    // Story 23.5 AC #8 — Yelp metadata side-channel (businessId + name +
    // alias + url + reviewsEndpointEntitled + address + categories + price
    // + isClaimed + isClosed + coordinates + tier + discoveredAt +
    // businessInput). Mirrors Moz 20.7 / CallRail 22.1 / Trustpilot 23.3
    // metadata-side-channel pattern; the `integrations` table has no
    // `metadata` JSON column. No `yelp:credentials:*` entry because Yelp
    // ships ONE secret (already stored in `oauth_tokens.access_token_encrypted`),
    // not the API-Key + API-Secret pair pattern Trustpilot uses.
    `yelp:meta:${integrationId}`,
  ];

  // **Code Review Pass 2 (High):** isolate failures per prefix so a KV
  // error on the new Semrush prefixes can't abort the original `metrics:`
  // deletion (or vice versa). Each prefix scan + each exact-key delete
  // gets its own try/catch.
  for (const prefix of prefixesToInvalidate) {
    try {
      let cursor: string | undefined;
      do {
        const listResult = await kv.list({ prefix, cursor });
        for (const key of listResult.keys) {
          try {
            await kv.delete(key.name);
            deletedCount++;
          } catch {
            // Best-effort deletion — continue with remaining keys
          }
        }
        cursor = listResult.list_complete ? undefined : listResult.cursor;
      } while (cursor);
    } catch (err) {
      logCacheEvent("cache_error", {
        cacheKey: prefix,
        tenantId,
        error: sanitizeErrorMessage(
          err instanceof Error ? err.message : String(err)
        ),
      });
    }
  }
  for (const exactKey of exactKeysToInvalidate) {
    try {
      await kv.delete(exactKey);
      // No counter increment — KV.delete on a missing key is a no-op,
      // not an error. We don't have a way to know whether the key existed.
    } catch (err) {
      logCacheEvent("cache_error", {
        cacheKey: exactKey,
        tenantId,
        error: sanitizeErrorMessage(
          err instanceof Error ? err.message : String(err)
        ),
      });
    }
  }

  logCacheEvent("cache_invalidate", {
    cacheKey: prefixesToInvalidate[0]!,
    tenantId,
    latencyMs: Date.now() - startTime,
  });

  return deletedCount;
}

/**
 * Minimal D1 binding interface used by the optional Story 20.7 GDPR sweep
 * — purposely loose so callers don't need `@cloudflare/workers-types`.
 */
export interface InvalidateTenantD1Binding {
  prepare(query: string): {
    bind(...values: unknown[]): {
      run(): Promise<unknown>;
    };
  };
}

/**
 * Invalidates all cache entries for a tenant.
 * Used during GDPR account deletion.
 *
 * Uses KV.list({ prefix }) + KV.delete() to find and remove all matching keys.
 *
 * **Story 20.7 (AC #16) — GDPR sweep for `moz_authority_snapshots`:** when a
 * `db` binding is supplied, also runs `DELETE FROM moz_authority_snapshots
 * WHERE tenant_id = ?` per ADR-008 right-to-erasure. The schema already has a
 * CASCADE FK on `tenants.id` which fires when the tenant row is deleted, but
 * this helper may be invoked from cache-only erase flows (admin recompute,
 * partial cleanup) where the tenant row is preserved — the explicit DELETE
 * keeps the snapshot table aligned with the contract claimed in
 * `packages/db/src/schema/moz-authority-snapshots.ts` and the migration header.
 *
 * Best-effort: a D1 failure is logged but does NOT block KV invalidation.
 */
export async function invalidateTenantCache(
  kv: KVStore,
  tenantId: string,
  db?: InvalidateTenantD1Binding
): Promise<number> {
  const startTime = Date.now();
  let deletedCount = 0;

  // Delete all tenant-prefixed `metrics:` KV entries via the cursor list-scan.
  const metricsPrefix = `metrics:${tenantId}:`;
  try {
    let cursor: string | undefined;
    do {
      const listResult = await kv.list({ prefix: metricsPrefix, cursor });
      for (const key of listResult.keys) {
        try {
          await kv.delete(key.name);
          deletedCount++;
        } catch {
          // Best-effort deletion — continue with remaining keys
        }
      }
      cursor = listResult.list_complete ? undefined : listResult.cursor;
    } while (cursor);
  } catch (err) {
    logCacheEvent("cache_error", {
      cacheKey: metricsPrefix,
      tenantId,
      error: sanitizeErrorMessage(
        err instanceof Error ? err.message : String(err)
      ),
    });
  }

  // Story 8.1 (AC #6): the overview rollup cache (`overview:{tenantId}`, the
  // buildAgencyOverviewCacheKey key) is swept alongside the per-integration
  // metrics cache so a GDPR tenant-deletion clears both. EXACT-key delete (NOT a
  // prefix list-scan over `overview:${tenantId}`) — a bare prefix would
  // over-match siblings (`overview:t1` matches `overview:t10`). A
  // get-then-delete is collision-proof AND keeps `deletedCount` exact: a tenant
  // with no overview key never increments, preserving the metrics-only
  // `deletedCount === 3` assertion in cache.test.ts. Best-effort try/catch.
  try {
    const overviewKey = buildAgencyOverviewCacheKey(tenantId);
    const existed = await kv.get(overviewKey);
    if (existed !== null) {
      await kv.delete(overviewKey);
      deletedCount++;
    }
  } catch (err) {
    logCacheEvent("cache_error", {
      cacheKey: buildAgencyOverviewCacheKey(tenantId),
      tenantId,
      error: sanitizeErrorMessage(
        err instanceof Error ? err.message : String(err)
      ),
    });
  }

  // Story 20.7 AC #16 + Story 20.9 AC #7 — GDPR sweep covers all 3 SEO
  // snapshot tables when a D1 binding is supplied. Best-effort per-table:
  // a single-table failure logs but does NOT block the other sweeps.
  // Round 1 Code Review M2-Edge refactor — extracted the per-table sweep
  // into a single loop to remove the previous 3-block code duplication.
  if (db) {
    const seoSnapshotTables = [
      "moz_authority_snapshots",
      "ahrefs_authority_snapshots",
      "semrush_authority_snapshots",
    ] as const;
    for (const table of seoSnapshotTables) {
      try {
        await db
          .prepare(`DELETE FROM ${table} WHERE tenant_id = ?`)
          .bind(tenantId)
          .run();
      } catch (err) {
        logCacheEvent("cache_error", {
          cacheKey: `${table}:${tenantId}`,
          tenantId,
          error: sanitizeErrorMessage(
            err instanceof Error ? err.message : String(err),
          ),
        });
      }
    }
  }

  // Delete refresh cooldown locks (cache:refresh_lock:* for tenant's integrations)
  // These are keyed by integrationId, not tenantId, so we can't prefix-scan.
  // They expire via 1-hour TTL and are non-sensitive, so this is best-effort.

  logCacheEvent("cache_invalidate", {
    cacheKey: metricsPrefix,
    tenantId,
    latencyMs: Date.now() - startTime,
  });

  return deletedCount;
}

// ============================================================================
// Refresh Cooldown
// ============================================================================

/**
 * Checks if a refresh cooldown is active for an integration.
 * Cooldown is enforced via a KV lock key with 1-hour TTL.
 *
 * @returns true if cooldown is active (refresh should be blocked)
 */
export async function checkRefreshCooldown(
  kv: KVStore,
  integrationId: string
): Promise<boolean> {
  const lockKey = `cache:refresh_lock:${integrationId}`;
  try {
    const existing = await kv.get(lockKey);
    return existing !== null;
  } catch {
    // Fail-open: if we can't check, allow the refresh
    return false;
  }
}

/**
 * Sets a refresh cooldown lock for an integration (1-hour TTL).
 * Called after a successful manual refresh.
 */
export async function setRefreshCooldown(
  kv: KVStore,
  integrationId: string
): Promise<void> {
  const lockKey = `cache:refresh_lock:${integrationId}`;
  try {
    await kv.put(lockKey, new Date().toISOString(), {
      expirationTtl: Math.round(REFRESH_COOLDOWN_MS / 1000),
    });
  } catch {
    // Fail-open: if we can't set the lock, the refresh still succeeds
  }
}
